import { useCallback, useEffect, useRef, useState } from 'react'

import { fetchCityPlanMissionBoundaries } from 'Module/graphQL'
import { readGraphQLData } from 'Components/PowerBanks/hooks'

const PAGE_SIZE = 2000
const DEBOUNCE_MS = 300

/**
 * The polygons the map should draw for the current viewport and filter,
 * fetched from the API page by page (cursor = skip) and merged into a cache
 * keyed by mission id. Mirrors what DistributionZonesComponent does for
 * zones: debounced on bounds, and a request id so a slow earlier response
 * never overwrites a later one.
 *
 * `version` (the context's missionsVersion) forces a refetch after any
 * mutation, so labels / statuses / polygons never go stale.
 *
 * @param {object} args
 * @param {string|number} args.cityPlanId
 * @param {?object} [args.filter] CityPlanMissionFilterInput
 * @param {?{minLat, minLng, maxLat, maxLng}} [args.bbox]
 * @param {number} [args.version]
 * @param {boolean} [args.enabled]
 * @returns {{items: Array<object>, loading: boolean, totalCount: number, refetch: function}}
 */
export const useMissionBoundaries = ({
  cityPlanId,
  filter,
  bbox,
  version = 0,
  enabled = true,
}) => {
  const [items, setItems] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const requestRef = useRef(0)
  const timerRef = useRef(null)
  const filterKey = JSON.stringify(filter || {})
  const bboxKey = bbox
    ? [bbox.minLat, bbox.minLng, bbox.maxLat, bbox.maxLng]
        .map(value => Number(value).toFixed(4))
        .join(',')
    : ''

  const load = useCallback(async () => {
    if (!cityPlanId || !enabled) return
    const requestId = (requestRef.current += 1)
    setLoading(true)
    try {
      const collected = []
      let skip = 0
      let total = 0
      let hasNextPage = true
      while (hasNextPage) {
        const data = await readGraphQLData(
          await fetchCityPlanMissionBoundaries({
            cityPlanId,
            filter: JSON.parse(filterKey),
            bbox: bbox || null,
            first: PAGE_SIZE,
            skip,
          }),
        )
        if (requestId !== requestRef.current) return
        const node = data.cityPlans.edges[0] && data.cityPlans.edges[0].node
        const page = node ? node.missionBoundaries : null
        if (!page) break
        collected.push(...(page.nodes || []))
        total = page.totalCount
        hasNextPage = page.pageInfo.hasNextPage
        skip += PAGE_SIZE
      }
      setItems(collected)
      setTotalCount(total)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Could not load the mission boundaries', error)
    } finally {
      if (requestId === requestRef.current) setLoading(false)
    }
    // bbox is folded into bboxKey so a new object with the same box is a no-op
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityPlanId, filterKey, bboxKey, enabled])

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(load, DEBOUNCE_MS)
    return () => clearTimeout(timerRef.current)
  }, [load, version])

  return { items, loading, totalCount, refetch: load }
}
